import React, { ComponentProps, Suspense, useEffect } from 'react'
import {
    Box,
    Button,
    ButtonProps,
    chakra,
    Flex,
    Grid,
    useDisclosure,
    useToast,
} from '@chakra-ui/react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { Megaphone } from '@emotion-icons/entypo'
import { ExternalLink } from '@emotion-icons/feather'
import { gql } from '@urql/core'
import { useMutation } from 'urql'
import { Company, Offer } from 'entities/company'
import { isJobOwner, Job } from 'entities/job'
import { hasRequiredPermissions } from 'entities/permission'
import { Anonymous, isAdminOrCompanyOwner, isGuest, LoggedInUser } from 'entities/user'
import { routes } from 'routes'
import { navigateToOldFrontEnd } from 'lib/navigation'
import { useAnalytics } from 'hooks/useAnalytics'
import useQuery from 'hooks/useQuery'
import { TrialStartedModal } from 'containers/job/components/TrialStartedModal'
import { UpgradeToPublishRoleModal } from 'containers/job/components/UpgradeToPublishRoleModal'
import { usePageRouter, useParams } from 'containers/job/hooks/useParams'
import { useTrackingPixels } from 'containers/job/tracking-pixels/context/WithTrackingPixels'
import {
    ApplicationCardCandidate,
    CandidateApplicationBasicData,
    FRAGMENT_CANDIDATE_APPLICATION_BASIC,
} from 'components/application/ApplicationCardCandidate'

const LazyApplicationChat = dynamic(
    () => import('containers/application-chat/ApplicationChat'),
    { suspense: true },
)

type CompanyForActionButtons = Pick<Company, 'id' | 'name'> & {
    pendingOffer?: Pick<Offer, 'recurringFee'>
    offer?: Pick<Offer, 'recurringFee'>
}
type JobForActionButtons = Pick<
    Job,
    'id' | 'slug' | 'published' | 'companyId' | 'tags' | 'affiliate'
> & {
    company: CompanyForActionButtons
}
type MeForActionButtons = Pick<
    LoggedInUser,
    'id' | 'isAdmin' | 'isCompanyUser' | 'isCandidate'
> & {
    company?: Pick<Company, 'permissions' | 'id'>
}

// copy of wh.job.events/process-publish-role-intention
function nextAction(
    companyThatCreatedJob: CompanyForActionButtons,
    me?: MeForActionButtons,
):
    | 'publish_job'
    | 'show_upgrade_to_publish_modal'
    | 'redirect_to_payment'
    | 'publish_and_show_free_trial_started_modal' {
    if (
        me?.isCompanyUser &&
        hasRequiredPermissions(
            ['can_publish', 'can_start_free_trial'],
            me?.company?.permissions,
        )
    ) {
        return 'publish_and_show_free_trial_started_modal'
    } else if (
        hasRequiredPermissions(['can_publish'], me?.company?.permissions) ||
        (me?.isAdmin &&
            (companyThatCreatedJob.pendingOffer || companyThatCreatedJob.offer))
    ) {
        return 'publish_job'
    } else if (me?.isAdmin) {
        return 'show_upgrade_to_publish_modal'
    }
    return 'redirect_to_payment'
}

export const MUTATION_UPDATE_JOB = gql<
    { update_job: Pick<Job, 'id' | 'published'> },
    { updateJob: Pick<Job, 'id' | 'published'> }
>`
    mutation update_job($updateJob: UpdateJobInput!) {
        update_job(update_job: $updateJob) {
            id
            published
            company {
                id
                permissions
            }
        }
    }
`

const ButtonViewApplications = ({
    me,
    job,
    variant,
}: {
    me: MeForActionButtons
    job: JobForActionButtons
    variant: ButtonProps['variant']
}) => {
    const href = React.useMemo(() => {
        if (me.isAdmin) {
            return routes.oldCompanyApplications({
                companyId: job.company.id,
                jobId: job.id,
            })
        } else if (me.isCompanyUser) {
            if (
                hasRequiredPermissions(['can_see_applications'], me.company?.permissions)
            ) {
                return routes.oldCompanyApplications({ jobId: job.id })
            } else {
                return routes.paymentPackages({ action: 'applications', job: job.id })
            }
        }
        return '#'
    }, [me.isAdmin, me.company?.permissions, job.company.id, job.id])

    return (
        // <Link href={href} passHref>
        <Button size={'md'} as={'a'} variant={variant} href={href}>
            View Applications
        </Button>
        // </Link>
    )
}

const ButtonEditJob = ({
    job,
    me,
}: {
    me: MeForActionButtons
    job: JobForActionButtons
}) => {
    const href = React.useMemo(() => {
        if (me.isAdmin) {
            return routes.editJob({ id: job.id })
        } else if (me.isCompanyUser) {
            if (hasRequiredPermissions(['can_edit_jobs'], me.company?.permissions)) {
                return routes.editJob({ id: job.id })
            } else {
                return routes.paymentPackages({ action: 'edit', job: job.id })
            }
        }
        return '#'
    }, [me.isAdmin, me.company?.permissions, job.slug, job.id])

    return (
        <Link href={href} passHref>
            <Button variant={'secondary'} size={'md'} as={'a'}>
                Edit
            </Button>
        </Link>
    )
}

const ButtonPublishJob = ({
    job,
    unpublishJob,
    publishJob,
    isUpdatingJob,
    shouldTellAboutTrial,
    variant,
}: {
    job: JobForActionButtons
    unpublishJob: () => void
    publishJob: () => void
    isUpdatingJob: boolean
    shouldTellAboutTrial: boolean
    variant: ButtonProps['variant']
}) => {
    return (
        <Button
            variant={variant}
            size={'md'}
            onClick={job.published ? unpublishJob : publishJob}
            isLoading={isUpdatingJob}
        >
            {job.published ? 'Unpublish' : 'Publish'}
            {shouldTellAboutTrial && !job.published && (
                <Box fontSize={'13px'} lineHeight={'18px'} ml="4px">
                    30d free trial
                </Box>
            )}
        </Button>
    )
}

const MegaphoneChakra = chakra(Megaphone)

const ActionButtonsCompanyOrAdmin = ({
    me,
    job,
    trackJobUnpublished,
    trackJobPublished,
}: {
    me: MeForActionButtons
    job: JobForActionButtons
    trackJobPublished: () => void
    trackJobUnpublished: () => void
}) => {
    const {
        isOpen: isOpenModalUpgradeToPublish,
        onClose: closeModalUpgradeToPublish,
        onOpen: openModalUpgradeToPublish,
    } = useDisclosure({
        id: 'upgrade-to-publish-modal',
    })
    const {
        isOpen: isOpenModalTrialStarted,
        onClose: closeModalTrialStarted,
        onOpen: openModalTrialStarted,
    } = useDisclosure({
        id: 'start-trial-modal',
    })

    const toast = useToast()

    const [{ fetching: isUpdatingJob }, updateJob] = useMutation(MUTATION_UPDATE_JOB)

    const unpublishJob = async () => {
        const result = await updateJob({ updateJob: { id: job.id, published: false } })
        if (result.error) {
            toast({
                title: `Sorry, something has happened`,
                status: 'error',
            })
        } else {
            trackJobUnpublished()
            toast({
                title: `Successfully unpublished`,
                status: 'success',
            })
        }
    }

    const publishJob = async () => {
        const result = await updateJob({ updateJob: { id: job.id, published: true } })
        if (result.error) {
            toast({
                title: `Sorry, something has happened`,
                status: 'error',
            })
            return Promise.reject(result.error)
        } else {
            trackJobPublished()
            toast({
                title: `Successfully published`,
                status: 'success',
            })
        }
    }

    const tryPublishJob = () => {
        if (me) {
            const action = nextAction(job.company, me)
            if (action === 'publish_job') {
                publishJob().catch(err => console.log(err))
            } else if (action === 'show_upgrade_to_publish_modal') {
                openModalUpgradeToPublish()
            } else if (action === 'publish_and_show_free_trial_started_modal') {
                publishJob()
                    .then(() => openModalTrialStarted())
                    .catch(err => console.log(err))
            } else if (action === 'redirect_to_payment') {
                navigateToOldFrontEnd(
                    routes.paymentPackages({ action: 'publish', job: job.id }),
                )
            }
        }
    }

    const viewType: 'for_admin' | 'for_company' | 'other' =
        (me?.isAdmin && 'for_admin') ||
        (me?.isCompanyUser && isJobOwner(job, me?.company) && 'for_company') ||
        'other'

    const layout: 'one_column' | 'multi_column' =
        viewType === 'for_company' && !job.published ? 'one_column' : 'multi_column'

    const canStartFreeTrial = hasRequiredPermissions(
        ['can_start_free_trial'],
        me.company?.permissions,
    )

    return (
        <>
            {layout === 'one_column' ? (
                <Flex gridGap={'16px'} flexDirection="column">
                    <ButtonPublishJob
                        variant={'primary'}
                        job={job}
                        unpublishJob={unpublishJob}
                        publishJob={tryPublishJob}
                        isUpdatingJob={isUpdatingJob}
                        shouldTellAboutTrial={canStartFreeTrial}
                    />
                    <ButtonEditJob job={job} me={me} />
                    {!canStartFreeTrial && (
                        <ButtonViewApplications job={job} me={me} variant={'secondary'} />
                    )}
                </Flex>
            ) : (
                <Flex gridGap={'16px'} flexDirection="column">
                    <ButtonViewApplications job={job} me={me} variant={'primary'} />
                    <Grid
                        flex={'max-content 1 0'}
                        gridGap={'12px'}
                        gridTemplateColumns={
                            viewType === 'for_admin' ? '1fr 1fr auto' : '1fr 1fr'
                        }
                    >
                        <ButtonEditJob job={job} me={me} />
                        <ButtonPublishJob
                            variant={'secondary'}
                            job={job}
                            unpublishJob={unpublishJob}
                            publishJob={tryPublishJob}
                            isUpdatingJob={isUpdatingJob}
                            shouldTellAboutTrial={canStartFreeTrial}
                        />
                        {viewType === 'for_admin' && (
                            <Button
                                variant={'secondary'}
                                size={'md'}
                                as={'a'}
                                href={routes.createPromotion({ id: job.id, type: 'job' })}
                            >
                                <MegaphoneChakra boxSize="20px" strokeWidth={1.5} />
                            </Button>
                        )}
                    </Grid>
                </Flex>
            )}
            <UpgradeToPublishRoleModal
                isOpen={isOpenModalUpgradeToPublish}
                onClose={closeModalUpgradeToPublish}
                companyId={job.company.id}
            />
            <TrialStartedModal
                isOpen={isOpenModalTrialStarted}
                onClose={closeModalTrialStarted}
            />
        </>
    )
}

const ExternalLinkChakra = chakra(ExternalLink)

const ApplyButton = ({
    action,
    isLoading,
    isDisabled,
}: {
    action: { url: string } | Pick<ComponentProps<typeof Button>, 'onClick'>
    isLoading: boolean | undefined
    isDisabled: boolean | undefined
}) => {
    if ('url' in action) {
        return (
            <Link href={action.url} passHref>
                <Button
                    as="a"
                    size="md"
                    gap="8px"
                    isLoading={isLoading}
                    isDisabled={isDisabled}
                    target="_blank"
                    rel="noopener"
                >
                    {'Apply Now'}
                    {<ExternalLinkChakra boxSize="22px" strokeWidth={1.5} />}
                </Button>
            </Link>
        )
    } else {
        return (
            <Button
                size="md"
                onClick={action.onClick}
                isLoading={isLoading}
                isDisabled={isDisabled}
            >
                {'Apply Now'}
            </Button>
        )
    }
}

const QUERY_CANDIDATE_APPLICATION = gql<
    { candidate_application?: CandidateApplicationBasicData },
    { job_id: Job['id'] }
>`
    query candidate_application($job_id: ID!) {
        candidate_application(job_id: $job_id) {
            ...CandidateApplicationBasic
        }
    }
    ${FRAGMENT_CANDIDATE_APPLICATION_BASIC}
`

const ActionButtonsPublicAndCandidate = ({
    me,
    job,
}: {
    me: MeForActionButtons | Anonymous
    job: JobForActionButtons
}) => {
    const { isOpen: isChatOpened, onClose: closeChat, onOpen: openChat } = useDisclosure({
        id: 'application-chat',
    })
    const urlParams = useParams()
    const pageRouter = usePageRouter()
    const { setShowApplicationTrackingPixels } = useTrackingPixels()
    const analytics = useAnalytics()

    const {
        isOpen: showLoader,
        onClose: disableLoader,
        onOpen: enableLoader,
    } = useDisclosure({
        id: 'apply-button-loader',
    })

    const [result, refetchApplicationUrql] = useQuery({
        query: QUERY_CANDIDATE_APPLICATION,
        variables: {
            job_id: job.id,
        },
        pause: !me?.isCandidate,
    })

    const application = result.data?.candidate_application
    const fetchingApplication = result.fetching
    const shouldOpenChat = !fetchingApplication && !application

    useEffect(() => {
        if (urlParams.apply && shouldOpenChat) {
            openChat()
        }
    }, [application, shouldOpenChat, urlParams.apply, openChat])

    const isPartnerJob = job.affiliate !== null

    if (result.error) {
        return null
    }

    return (
        <>
            {application ? (
                <ApplicationCardCandidate
                    variant="basic"
                    application={application}
                    onUpdate={() => null}
                />
            ) : (
                <ApplyButton
                    action={
                        isPartnerJob
                            ? { url: job.affiliate!.url }
                            : {
                                  onClick: () => {
                                      openChat()
                                      enableLoader()
                                  },
                              }
                    }
                    isLoading={showLoader || fetchingApplication}
                    isDisabled={showLoader || fetchingApplication}
                />
            )}
            {isChatOpened && (
                <Suspense>
                    <LazyApplicationChat
                        onClose={() => {
                            closeChat()
                            pageRouter.removeApplyParam()
                        }}
                        isOpen={isChatOpened}
                        job={job}
                        handleSuccessfulApplication={() => {
                            refetchApplicationUrql({ requestPolicy: 'network-only' })
                            setShowApplicationTrackingPixels(true)
                            analytics?.track('Job Applied', {
                                id: job.id,
                                slug: job.slug,
                            })
                        }}
                        disableLoader={disableLoader}
                        isGuest={isGuest(me)}
                    />
                </Suspense>
            )}
        </>
    )
}

export const ActionButtons = ({
    me,
    job,
    trackJobUnpublished,
    trackJobPublished,
}: {
    me: MeForActionButtons | Anonymous
    job: JobForActionButtons
    trackJobPublished: () => void
    trackJobUnpublished: () => void
}) => {
    return (
        <>
            {(isGuest(me) || me?.isCandidate) && (
                <ActionButtonsPublicAndCandidate me={me} job={job} />
            )}
            {me && isAdminOrCompanyOwner(me, job.companyId) && (
                <ActionButtonsCompanyOrAdmin
                    me={me}
                    job={job}
                    trackJobPublished={trackJobPublished}
                    trackJobUnpublished={trackJobUnpublished}
                />
            )}
        </>
    )
}
